/**
 * Browser outbox (gateway mode on one screen; M05, SF-18). A frame that
 * cannot reach the API because the network is down is kept here, saved in
 * this browser, and replayed in order through `POST /frames/batch` when the
 * line is back, with its original time and frame id (the API ignores
 * duplicates). Frames the server refuses on replay are reported, not retried.
 */
import {
  FRAME_BATCH_MAX,
  FrameBatchResultSchema,
  type FrameBatchResult,
  type LightboardFrame,
  type Priority,
} from '@velora/contracts/lightboard';
import { FrameOutbox, retryDelayMs, type OutboxSnapshot } from '@velora/domain/lightboard';
import { ApiError, type HttpClient } from '@velora/sdk';
import { useSyncExternalStore } from 'react';

const KEY = 'velora.lightboard.outbox';

function load(): OutboxSnapshot | undefined {
  try {
    const raw = globalThis.localStorage?.getItem(KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as OutboxSnapshot;
    return Array.isArray(parsed.items) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

function save(box: FrameOutbox): void {
  try {
    if (box.size === 0) globalThis.localStorage?.removeItem(KEY);
    else globalThis.localStorage?.setItem(KEY, JSON.stringify(box.snapshot()));
  } catch {
    // Storage blocked (private window): the queue still works for this tab.
  }
}

let box = new FrameOutbox(undefined, load());
let version = 0;
const listeners = new Set<() => void>();

function changed(): void {
  version += 1;
  save(box);
  for (const l of listeners) l();
}

export function queueFrame(locationId: string, frame: LightboardFrame, priority: Priority): void {
  if (box.push({ locationId, frame, priority, queuedAt: Date.now() })) changed();
}

export function outboxSize(): number {
  return box.size;
}

export function useOutboxSize(): { size: number; dropped: number } {
  useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => version,
    () => version,
  );
  return { size: box.size, dropped: box.dropped };
}

export interface DrainReport {
  sent: number;
  rejected: { frameId: string; message: string }[];
}

/**
 * A refusal of the whole batch that retrying cannot fix (subscription ended,
 * permission removed, a frame the server will never accept). Signed out
 * (401), timeouts (408) and rate limits (429) are worth retrying.
 */
function finalRefusal(err: unknown): err is ApiError {
  return (
    err instanceof ApiError &&
    err.status >= 400 &&
    err.status < 500 &&
    ![401, 408, 429].includes(err.status)
  );
}

function refusalText(err: ApiError): string {
  const body = err.body as { error?: { message?: unknown } } | undefined;
  const message = body?.error?.message;
  return typeof message === 'string' && message ? message : `The server said ${err.status}.`;
}

/**
 * Sends everything queued, office by office, in batches. Stops at the first
 * network failure (the caller retries later with `retryDelayMs`). A batch
 * the server refuses outright is reported and dropped, so it cannot block
 * the queue forever.
 */
export async function drainOutbox(clientFor: (office: string) => HttpClient): Promise<DrainReport> {
  const report: DrainReport = { sent: 0, rejected: [] };
  for (const locationId of box.locations()) {
    for (;;) {
      const batch = box.peek(locationId, FRAME_BATCH_MAX);
      if (batch.length === 0) break;
      let res: FrameBatchResult;
      try {
        res = await clientFor(locationId).request('/lightboard/frames/batch', {
          method: 'POST',
          body: { source: 'replay', frames: batch.map((b) => b.frame) },
          schema: FrameBatchResultSchema,
        });
      } catch (err) {
        if (!finalRefusal(err)) throw err;
        const message = refusalText(err);
        box.ack(batch.map((b) => b.frame.frameId));
        for (const b of batch) report.rejected.push({ frameId: b.frame.frameId, message });
        changed();
        continue;
      }
      box.ack(res.results.map((r) => r.frameId));
      report.sent += res.applied + res.duplicates;
      for (const r of res.results) {
        if (r.outcome === 'rejected') {
          report.rejected.push({ frameId: r.frameId, message: r.message ?? 'Not applied.' });
        }
      }
      changed();
    }
  }
  return report;
}

export { retryDelayMs };

/** Test hook: start from an empty outbox. */
export function resetOutboxForTests(): void {
  box = new FrameOutbox();
  changed();
}
